import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Sparkles, FileDown, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, StatCard } from '../components/common';
import { exportPDF } from '../utils/exportPDF';
import api from '../api/client';

const TYPE_COLORS = ['var(--kmt-red)', 'var(--kmt-amber)', 'var(--kmt-blue)', 'var(--kmt-gold)', 'var(--kmt-green)', 'var(--kmt-idle)', '#a855f7'];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthOptions() {
  const opts = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const val = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ val, label: dt.toLocaleString(undefined, { month: 'long', year: 'numeric' }) });
  }
  return opts;
}
function fmtMins(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
}

export default function MachineProblemReport() {
  const [month, setMonth] = useState(currentMonth());
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState('');
  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ai, setAi] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setAi('');
    api.problemReport(month, machine || undefined).then(setReport).catch(() => setReport(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, machine]);
  useEffect(() => { api.problemTrend(6).then(setTrend).catch(() => setTrend(null)); }, []);
  useEffect(() => { api.listMachines().then(setMachines).catch(() => {}); }, []);

  const cur = report?.currency || '₹';
  const money = (v) => `${cur}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const trendMachines = (trend?.machines || []).filter((m) => !machine || m.machine === machine);

  const runAI = () => {
    setAiLoading(true);
    api.problemReportAI(month, machine || undefined)
      .then((r) => setAi(r.analysis || ''))
      .catch(() => toast.error('Could not generate AI analysis'))
      .finally(() => setAiLoading(false));
  };

  const exportReport = () => {
    if (!report || !report.machines.length) return;
    const rows = [];
    report.machines.forEach((m) => {
      m.byType.forEach((t) => rows.push({
        machine: `${m.machine} · ${m.name}`, problem: t.type, reason: t.reason || '—', count: t.count, downtime: fmtMins(t.downtime),
        top: m.topProblem ? m.topProblem.type : '—', total: m.incidents,
      }));
    });
    exportPDF(`kmt_problem_report_${report.month}.pdf`, `Machine Problem Report · ${report.monthLabel}`, [
      { label: 'Machine', get: (r) => r.machine },
      { label: 'Problem Type', get: (r) => r.problem },
      { label: 'Main Reason', get: (r) => r.reason },
      { label: 'Count', get: (r) => String(r.count) },
      { label: 'Downtime', get: (r) => r.downtime },
    ], rows);
    toast.success('Problem report exported (PDF)');
  };

  const maxOverall = Math.max(1, ...(report?.overallByType || []).map((t) => t.count));

  return (
    <div>
      <PageHeader icon={AlertTriangle} title="Machine Problem Report" subtitle="Monthly breakdown analysis — which problems recur on each machine, and why"
        right={<>
          <select className="kmt-input" style={{ minWidth: 170 }} value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="problem-report-machine">
            <option value="">All Machines</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
          </select>
          <select className="kmt-input" style={{ minWidth: 160 }} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="problem-report-month">
            {monthOptions().map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={load} data-testid="problem-report-refresh"><RefreshCw size={15} />Refresh</button>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={exportReport} disabled={!report?.machines?.length} data-testid="problem-report-export"><FileDown size={16} />PDF</button>
        </>} />

      <div className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={26} /></div>
        ) : !report || report.totalIncidents === 0 ? (
          <div className="kmt-panel p-10 text-center" data-testid="problem-report-empty">
            <AlertTriangle size={30} style={{ color: 'var(--kmt-muted)' }} className="mx-auto mb-3" />
            <div className="font-display font-semibold text-lg">No problems logged for {report?.monthLabel || 'this month'}</div>
            <div className="text-sm mt-1" style={{ color: 'var(--kmt-muted)' }}>Once machines report downtime and problems, this report shows the most frequent issue per machine plus an AI root-cause analysis.</div>
          </div>
        ) : (
          <>
            {report.overTargetCount > 0 && (
              <div className="rounded-lg px-4 py-3 flex items-center gap-2 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--kmt-red)' }} data-testid="pr-over-target-banner">
                <AlertTriangle size={16} /><b>{report.overTargetCount}</b> machine{report.overTargetCount === 1 ? '' : 's'} exceeded the monthly downtime target this month.
              </div>
            )}
            {/* summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard label="Total Incidents" value={report.totalIncidents} accent="var(--kmt-red)" testId="pr-total-incidents" />
              <StatCard label="Total Downtime" value={fmtMins(report.totalDowntimeMinutes)} accent="var(--kmt-amber)" testId="pr-total-downtime" />
              <StatCard label="Money Lost" value={report.downtimeCostPerHour > 0 ? money(report.totalCostLost) : '—'} accent="var(--kmt-red)" testId="pr-money-lost" />
              <StatCard label="Most Frequent" value={report.mostFrequentType ? report.mostFrequentType.type : '—'} accent="var(--kmt-gold)" testId="pr-most-frequent" />
              <StatCard label="Machines Affected" value={report.machines.length} accent="var(--kmt-blue)" testId="pr-machines-affected" />
            </div>
            {report.downtimeCostPerHour <= 0 && (
              <div className="text-xs -mt-2" style={{ color: 'var(--kmt-muted)' }} data-testid="pr-cost-hint">
                Set a "Downtime Cost / hour" in Settings → Regional to see money lost per machine.
              </div>
            )}

            {/* 6-month trend */}
            {trend && trendMachines.length > 0 && (
              <div className="kmt-panel p-6" data-testid="pr-trend-panel">
                <div className="kmt-label mb-4 flex items-center gap-2"><TrendingUp size={14} />Problem Count Trend · last {trend.months.length} months</div>
                <div className="space-y-3" data-testid="pr-trend-rows">
                  {trendMachines.map((m) => {
                    const maxC = Math.max(1, ...m.counts);
                    const arrow = m.trend === 'down' ? '▼' : m.trend === 'up' ? '▲' : '▬';
                    const arrowColor = m.trend === 'down' ? 'var(--kmt-green)' : m.trend === 'up' ? 'var(--kmt-red)' : 'var(--kmt-muted)';
                    return (
                      <div key={m.machine} className="flex items-center gap-3" data-testid={`pr-trend-${m.machine}`}>
                        <div className="w-40 shrink-0 truncate text-sm">
                          <span className="font-mono-k text-xs" style={{ color: 'var(--kmt-gold)' }}>{m.machine}</span>
                          <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>{m.name}</span>
                        </div>
                        <div className="flex items-end gap-1.5 flex-1" style={{ height: 44 }}>
                          {m.counts.map((c, i) => (
                            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${trend.months[i]}: ${c}`}>
                              <span style={{ fontSize: 9, color: 'var(--kmt-muted-2)' }}>{c || ''}</span>
                              <div className="w-full rounded-t" style={{ height: `${(c / maxC) * 30}px`, minHeight: c ? 3 : 1, background: i === m.counts.length - 1 ? 'var(--kmt-gold)' : 'var(--kmt-idle)' }} />
                              <span style={{ fontSize: 8, color: 'var(--kmt-muted-2)' }}>{trend.months[i]}</span>
                            </div>
                          ))}
                        </div>
                        <div className="w-16 shrink-0 text-right text-sm font-mono-k" style={{ color: arrowColor }} title={`Trend ${m.trend} vs previous month`}>{arrow} {m.total}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-xs mt-3" style={{ color: 'var(--kmt-muted)' }}>▼ green = fewer problems than last month (improving) · ▲ red = more (worsening). Last bar = current month.</div>
              </div>
            )}

            {/* AI analysis */}
            <div className="kmt-panel p-6" data-testid="pr-ai-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="kmt-label flex items-center gap-2"><Sparkles size={14} style={{ color: 'var(--kmt-gold)' }} />AI Root-Cause Analysis</div>
                <button className="kmt-btn-gold flex items-center gap-2" onClick={runAI} disabled={aiLoading} data-testid="pr-ai-generate">
                  {aiLoading ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}{ai ? 'Regenerate' : 'Analyse why'}
                </button>
              </div>
              {ai ? (
                <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--kmt-text)' }} data-testid="pr-ai-text">{ai}</div>
              ) : (
                <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>Click "Analyse why" to generate an AI explanation of why these problems recur and preventive-maintenance recommendations.</div>
              )}
            </div>

            {/* overall distribution */}
            <div className="kmt-panel p-6">
              <div className="kmt-label mb-2 flex items-center gap-2"><TrendingUp size={14} />Problem Frequency (all machines)</div>
              {report.mostFrequentReason && (
                <div className="text-xs mb-4" style={{ color: 'var(--kmt-muted)' }} data-testid="pr-top-reason-overall">
                  Most recurring reason: <b style={{ color: 'var(--kmt-red)' }}>"{report.mostFrequentReason.reason}"</b> ({report.mostFrequentReason.count}×)
                </div>
              )}
              <div className="space-y-3" data-testid="pr-overall-distribution">
                {report.overallByType.map((t, i) => (
                  <div key={t.type}>
                    <div className="flex justify-between text-xs mb-1"><span>{t.type}{t.reason ? <span style={{ color: 'var(--kmt-muted)' }}> — {t.reason}</span> : ''}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{t.count}× · {fmtMins(t.downtime)}</span></div>
                    <div className="h-2.5 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2.5 rounded-full" style={{ width: `${(t.count / maxOverall) * 100}%`, background: TYPE_COLORS[i % TYPE_COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            </div>

            {/* per-machine cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pr-machine-cards">
              {report.machines.map((m) => {
                const maxM = Math.max(1, ...m.byType.map((t) => t.count));
                return (
                  <div key={m.machine} className="kmt-panel p-5" data-testid={`pr-machine-${m.machine}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-display font-semibold">{m.name}</div>
                      <span className="font-mono-k text-xs" style={{ color: 'var(--kmt-gold)' }}>{m.machine}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-3" style={{ color: 'var(--kmt-muted)' }}>
                      <span><b style={{ color: 'var(--kmt-red)' }}>{m.incidents}</b> incidents</span>
                      <span><b style={{ color: 'var(--kmt-amber)' }}>{fmtMins(m.downtimeMinutes)}</b> down</span>
                      <span><b>{m.openCount}</b> still open</span>
                      {report.downtimeCostPerHour > 0 && <span><b style={{ color: 'var(--kmt-red)' }}>{money(m.costLost)}</b> lost</span>}
                    </div>
                    {m.targetHours > 0 && (
                      <div className="text-xs mb-3 rounded-lg px-2 py-1.5 flex items-center justify-between"
                        style={m.overTarget
                          ? { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--kmt-red)' }
                          : { background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', color: 'var(--kmt-green)' }}
                        data-testid={`pr-target-${m.machine}`}>
                        <span>Target {m.targetHours}h/mo · used {m.downtimeHours}h</span>
                        <span style={{ fontWeight: 700 }}>{m.overTarget ? `⚠ OVER by ${Math.round((m.downtimeHours - m.targetHours) * 10) / 10}h` : 'WITHIN TARGET'}</span>
                      </div>
                    )}
                    {m.topProblem && (
                      <div className="text-xs mb-3 rounded-lg p-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: 'var(--kmt-red)' }}>
                        Most frequent: <b>{m.topProblem.type}</b> ({m.topProblem.count}× · {fmtMins(m.topProblem.downtime)})
                        {m.topReason && <div className="mt-0.5" style={{ color: 'var(--kmt-muted)' }} data-testid={`pr-top-reason-${m.machine}`}>Recurring: "{m.topReason.reason}" ({m.topReason.count}×)</div>}
                      </div>
                    )}
                    <div className="space-y-2">
                      {m.byType.map((t, i) => (
                        <div key={t.type}>
                          <div className="flex justify-between text-xs mb-0.5"><span>{t.type}{t.reason ? <span style={{ color: 'var(--kmt-muted)' }}> — {t.reason}</span> : ''}</span><span className="font-mono-k" style={{ color: 'var(--kmt-muted)' }}>{t.count}×</span></div>
                          <div className="h-2 rounded-full" style={{ background: '#0e0e10' }}><div className="h-2 rounded-full" style={{ width: `${(t.count / maxM) * 100}%`, background: TYPE_COLORS[i % TYPE_COLORS.length] }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
